import { useState, useCallback, useRef, useEffect, type FormEvent } from 'react';
import { invoiceNumber } from './accessKey';
import { EmissionProgressPanel } from './EmissionProgressPanel';
import { useNavigate } from '@tanstack/react-router';
import { Button, Input, Select, Modal, ErrorSummary, FormSection } from '@nexora/design-system';
import { useAuth } from '../../shared/auth/AuthContext';
import { useFetchParties, useFetchProducts, useFetchCompanies, useEmitFiscalInvoice, useFetchBranches, useFetchNumberSequences, generateNextNumber, type EmitInvoiceInput, type Product } from './useFiscalInvoices';
import { formatCurrency } from '../../shared/utils/format';

/* ── Types ── */

type DocType = 'NFE' | 'NFSE' | 'NFCE';

interface InvoiceItem {
  productId: string;
  description: string;
  ncm: string;
  cfop: string;
  cst: string;
  quantity: number;
  unitValue: number;
  discount: number;
  icms: number;
  pis: number;
  cofins: number;
  ipi: number;
  iss: number;
}

interface TaxEstimate {
  icms: number;
  pis: number;
  cofins: number;
  ipi: number;
  iss: number;
}

const DEFAULT_TAX: TaxEstimate = {
  icms: 0,
  pis: 0,
  cofins: 0,
  ipi: 0,
  iss: 0,
};

/* ── Helpers ── */

const docTypeLabel: Record<DocType, string> = {
  NFE: 'NF-e (Nota Fiscal Eletrônica)',
  NFSE: 'NFS-e (Nota Fiscal de Serviço)',
  NFCE: 'NFC-e (Nota Fiscal ao Consumidor)',
};

const commonCfops: { label: string; value: string }[] = [
  { label: '5.102 - Venda p/ contribuinte', value: '5102' },
  { label: '5.104 - Venda p/ não contribuinte', value: '5104' },
  { label: '5.105 - Venda p/ contribuinte (NFC-e)', value: '5105' },
  { label: '5.201 - Devolução de venda', value: '5201' },
  { label: '6.101 - Venda p/ contribuinte (interestadual)', value: '6101' },
  { label: '5.901 - Remessa p/ industrialização', value: '5901' },
  { label: '6.901 - Remessa p/ industrialização (interest.)', value: '6901' },
  { label: '1.101 - Compra p/ industrialização', value: '1101' },
  { label: '1.102 - Compra p/ comercialização', value: '1102' },
  { label: '2.201 - Transferência de estoque', value: '2201' },
];

const commonCstsNfe: string[] = [
  '00', '10', '20', '40', '41', '50', '51', '60', '70', '90',
];

/* ── Component ── */

export function FiscalInvoiceEmitPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();

  /* ── Form state ── */
  const [docType, setDocType] = useState<DocType>('NFE');
  const [emitterId, setEmitterId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [destinatarySearch, setDestinatarySearch] = useState('');
  const [destinataryId, setDestinataryId] = useState('');
  const [destinataryName, setDestinataryName] = useState('');
  const [destinataryDoc, setDestinataryDoc] = useState('');
  const [showDestinataryDropdown, setShowDestinataryDropdown] = useState(false);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [successResult, setSuccessResult] = useState<{
    accessKey?: string;
    number?: string;
    /* Id do documento: e a chave para acompanhar a emissao em
       `/fiscal/emission-requests`. */
    invoiceId?: string;
  } | null>(null);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const destinataryRef = useRef<HTMLDivElement>(null);
  const productRef = useRef<HTMLDivElement>(null);

  /* ── Queries ── */

  const { data: partiesData, isLoading: partiesLoading } = useFetchParties(tenantId, destinatarySearch);
  const { data: productsData, isLoading: productsLoading } = useFetchProducts(tenantId, productSearchTerm);
  const { data: companiesData } = useFetchCompanies(tenantId);
  const { data: branchesData } = useFetchBranches(emitterId);
  const { data: sequencesData } = useFetchNumberSequences(tenantId, emitterId);
  const emitMutation = useEmitFiscalInvoice(tenantId);

  const branches = branchesData ?? [];
  const sequences = sequencesData ?? [];

  /* ── Click-away handlers ── */

  useEffect(() => {
    function handleClick(e: MouseEvent): void {
      if (destinataryRef.current && !destinataryRef.current.contains(e.target as Node)) {
        setShowDestinataryDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent): void {
      if (productRef.current && !productRef.current.contains(e.target as Node)) {
        setShowProductDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* ── Items logic ── */

  const parties = partiesData?.data ?? [];
  /* `GET /products` devolve array puro (StockPage, ProductsPage e o catalogo
     offline do PDV consomem assim). Este consumidor lia `.data` e ficava
     sempre vazio: a busca de produto do item da nota nunca achava nada. */
  const products = productsData ?? [];
  const companies = companiesData?.data ?? [];

  const addItem = useCallback((): void => {
    setItems((prev) => [
      ...prev,
      {
        productId: '',
        description: '',
        ncm: '',
        cfop: '5102',
        cst: '00',
        quantity: 1,
        unitValue: 0,
        discount: 0,
        ...DEFAULT_TAX,
      },
    ]);
  }, []);

  const updateItem = useCallback(
    (index: number, field: keyof InvoiceItem, value: string | number): void => {
      setFieldErrors((prev) => {
        const key = `item-${index}-${field}`;
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setItems((prev) => {
        const updated = prev.map((item, i) => (i === index ? { ...item } : item));
        const item = updated[index];
        if (!item) return prev;
        (item as unknown as Record<string, unknown>)[field] = value;

        // Recalculate taxes when quantity, unitValue, or discount changes
        if (['quantity', 'unitValue', 'discount'].includes(field)) {
          const qty = item.quantity;
          const unit = item.unitValue;
          const disc = item.discount;
          const gross = qty * unit;
          const net = Math.max(gross - disc, 0);

          if (item.cst === '00' || item.cst === '20' || item.cst === '40') {
            item.icms = net * 0.18;
          } else if (item.cst === '10') {
            item.icms = net * 0.12;
          } else if (item.cst === '41' || item.cst === '60') {
            item.icms = 0;
          } else {
            item.icms = net * 0.18;
          }

          item.pis = net * 0.0165;
          item.cofins = net * 0.076;
          if (item.ncm.startsWith('84') || item.ncm.startsWith('85')) {
            item.ipi = net * 0.05;
          } else {
            item.ipi = net * 0.10;
          }
          item.iss = 0;
        }

        updated[index] = item;
        return updated;
      });
    },
    [],
  );

  const removeItem = useCallback((index: number): void => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const selectProduct = useCallback(
    (index: number, product: Product): void => {
      setItems((prev) => {
        const updated = prev.map((item, i) => (i === index ? { ...item } : item));
        const item = updated[index];
        if (!item) return prev;
        item.productId = product.id;
        item.description = product.name;
        item.ncm = product.ncmCode ?? '';
        item.unitValue = product.price;
        item.quantity = 1;
        item.discount = 0;

        // Trigger tax recalculation
        const net = product.price;
        if (item.cst === '00' || item.cst === '20' || item.cst === '40') {
          item.icms = net * 0.18;
        } else if (item.cst === '10') {
          item.icms = net * 0.12;
        } else if (item.cst === '41' || item.cst === '60') {
          item.icms = 0;
        } else {
          item.icms = net * 0.18;
        }
        item.pis = net * 0.0165;
        item.cofins = net * 0.076;
        if (item.ncm.startsWith('84') || item.ncm.startsWith('85')) {
          item.ipi = net * 0.05;
        } else {
          item.ipi = net * 0.10;
        }
        item.iss = 0;

        updated[index] = item;
        return updated;
      });

      setShowProductDropdown(false);
      setProductSearchTerm('');
      setProductSearchIndex(null);
    },
    [],
  );

  /* ── Totals ── */

  const totals = items.reduce(
    (acc, item) => {
      const gross = item.quantity * item.unitValue;
      const net = Math.max(gross - item.discount, 0);
      acc.totalValue += net;
      acc.totalDiscount += item.discount;
      acc.icms += item.icms;
      acc.pis += item.pis;
      acc.cofins += item.cofins;
      acc.ipi += item.ipi;
      acc.iss += item.iss;
      return acc;
    },
    { totalValue: 0, totalDiscount: 0, icms: 0, pis: 0, cofins: 0, ipi: 0, iss: 0 },
  );

  const grandTotal =
    totals.totalValue + totals.icms + totals.pis + totals.cofins + totals.ipi + totals.iss;

  /* ── Validation ── */

  const validate = useCallback((): boolean => {
    const newErrors: string[] = [];
    const newFieldErrors: Record<string, string> = {};

    if (!emitterId) {
      newFieldErrors['emitter'] = 'Selecione o emitente';
    }
    if (!branchId && branches.length > 0) {
      newFieldErrors['branch'] = 'Selecione a filial';
    }
    if (!destinataryId && docType !== 'NFCE') {
      newFieldErrors['destinatary'] = 'Selecione o destinatário';
    }
    if (items.length === 0) {
      newErrors.push('Adicione pelo menos um item');
    }
    items.forEach((item, index) => {
      if (item.quantity <= 0) {
        newFieldErrors[`item-${index}-quantity`] = 'Quantidade deve ser maior que zero';
      }
      if (item.unitValue <= 0) {
        newFieldErrors[`item-${index}-unitValue`] = 'Valor unitário deve ser maior que zero';
      }
      if (!item.description) {
        newFieldErrors[`item-${index}-description`] = 'Descrição obrigatória';
      }
      if (item.ncm && !/^\d{8}$/.test(item.ncm)) {
        newFieldErrors[`item-${index}-ncm`] = 'NCM deve ter 8 dígitos';
      }
    });
    if (docType === 'NFCE' && !destinataryId && items.some((item) => item.unitValue > 500)) {
      newErrors.push('NFC-e sem CPF do consumidor não pode exceder R$ 500,00 por item');
    }

    setErrors(newErrors);
    setFieldErrors(newFieldErrors);
    return newErrors.length === 0 && Object.keys(newFieldErrors).length === 0;
  }, [emitterId, branchId, destinataryId, docType, items, branches.length]);

  /* ── Submit ── */

  /* Ponte entre a chave de `fieldErrors` e o campo real na tela: o
     `ErrorSummary` precisa do `id` do elemento para focar e rolar ate ele. */
  const FIELD_LABELS: Record<string, string> = {
    emitter: 'Emitente',
    branch: 'Filial (Estabelecimento)',
    destinatary: 'Destinatario',
  };

  const fieldIdFor = (key: string): string => {
    const item = /^item-(\d+)-(\w+)$/.exec(key);
    return item ? `emit-item-${item[1]}-${item[2]}` : `emit-${key}`;
  };

  const fieldLabelFor = (key: string): string => {
    const item = /^item-(\d+)-(\w+)$/.exec(key);
    if (!item) return FIELD_LABELS[key] ?? key;
    const names: Record<string, string> = {
      description: 'Descricao',
      ncm: 'NCM',
      quantity: 'Quantidade',
      unitValue: 'Valor unitario',
    };
    return `Item ${Number(item[1]) + 1} \u2014 ${names[item[2] ?? ''] ?? item[2]}`;
  };

  /* Erro de campo e erro global no mesmo resumo: o usuario ve de uma vez
     quantos problemas existem, e quem usa leitor de tela tambem. */
  const summaryErrors = [
    ...Object.entries(fieldErrors).map(([key, message]) => ({
      fieldId: fieldIdFor(key),
      label: fieldLabelFor(key),
      message,
    })),
    ...errors.map((message) => ({ fieldId: '', label: 'Formulario', message })),
  ];

  const clearFieldError = useCallback((field: string): void => {
    setFieldErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const selectedCompany = companies.find((c) => c.id === emitterId);
      const selectedBranch = branches.find((b) => b.id === branchId);
      const selectedDest = parties.find((p) => p.id === destinataryId);
      const destName = selectedDest?.name ?? destinataryName;

      // Sequência de numeração: busca a de NF-e (55) para a empresa/filial
      const invoiceSeq = sequences.find(
        (s) => s.entity === 'INVOICE' || s.entity === 'NFE' || s.entity === 'NF-E',
      ) ?? sequences[0];
      let generatedNumber: string | undefined;
      if (invoiceSeq) {
        const gen = await generateNextNumber(invoiceSeq.id, selectedBranch?.code);
        generatedNumber = gen.generatedNumber;
      }

      const payload: EmitInvoiceInput = {
        companyId: emitterId,
        branchId,
        type: docType,
        ide: generatedNumber
          ? {
              serie: Number(invoiceSeq?.format?.match(/\d+/) ?? '1') || 1,
              nNF: Number(generatedNumber) || 0,
            }
          : undefined,
        emitter: {
          cnpj: selectedCompany?.cnpj ?? '',
          ie: selectedCompany?.ie ?? '',
          companyName: selectedCompany?.name ?? '',
          tradeName: selectedCompany?.tradeName,
          address: selectedCompany?.address ?? {
            street: '', number: '', neighborhood: '', city: '', state: '', zipCode: '',
          },
          crt: selectedCompany?.crt,
        },
        destinatary: {
          cnpj: selectedDest?.cnpj,
          cpf: selectedDest?.cpf,
          name: destName,
          ie: selectedDest?.ie,
          email: selectedDest?.email,
          address: selectedDest?.address ?? {
            street: '', number: '', neighborhood: '', city: '', state: '', zipCode: '',
          },
        },
        items: items.map(
          ({ productId, description, ncm, cfop, quantity, unitValue, discount }) => ({
            productId: productId || undefined,
            productCode: productId || description,
            productName: description,
            ncmCode: ncm || '00000000',
            cfopCode: cfop || (docType === 'NFE' ? '5102' : undefined),
            uom: 'UN',
            quantity,
            unitPrice: unitValue,
            discount: discount || undefined,
          }),
        ),
        operationType: 'SALE',
        regimeTributario: selectedCompany?.crt === '1' || selectedCompany?.crt === '4' ? 'SN' : 'LP',
        payment: docType === 'NFCE'
          ? { detPag: [{ tPag: '01', vPag: items.reduce((s, i) => s + i.unitValue * i.quantity - (i.discount ?? 0), 0) }] }
          : undefined,
      };

      const res = await emitMutation.mutateAsync(payload);
      setSuccessResult({
        invoiceId: res.invoice?.id,
        accessKey: res.invoice?.accessKey ?? res.accessKey,
        number: (() => {
          const n = invoiceNumber(res.invoice?.accessKey ?? res.accessKey);
          return n === null ? undefined : String(n);
        })(),
      });
      setItems([]);
      setErrors([]);
      setShowConfirmModal(false);
    } catch (error) {
      setErrors([(error as Error).message]);
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
      setShowConfirmModal(false);
    }
  }, [validate, docType, emitterId, branchId, destinataryId, items, navigate, emitMutation, companies, parties, branches, sequences, destinataryName, destinataryDoc]);

  /* ── Render ── */

  return (
    <div className="fiscal-emit-page">
      {/* Breadcrumb */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <a href="/fiscal/invoices" onClick={(e) => { e.preventDefault(); navigate({ to: '/fiscal/invoices' }).catch(console.error); }}>
          Documentos Fiscais
        </a>
        <span className="breadcrumb-separator">/</span>
        <span aria-current="page">Emissão de Documento Fiscal</span>
      </nav>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Emissão de Documento Fiscal</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Emissão de NF-e, NFS-e e NFC-e.</p>
        </div>
      </div>

      {successResult && (
        /* O banner anterior anunciava "emitido com sucesso" no instante em que
           o POST respondia — antes de a SEFAZ dizer qualquer coisa, e uma nota
           pode voltar rejeitada. Este painel mostra a situacao real da
           emissao. */
        <EmissionProgressPanel
          fiscalDocumentId={successResult.invoiceId ?? null}
          accessKey={successResult.accessKey ?? null}
          numero={successResult.number ?? null}
          onVerLista={() => navigate({ to: '/fiscal/invoices' }).catch(console.error)}
        />
      )}

      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (validate()) {
            setShowConfirmModal(true);
          }
        }}
      >
        {/* Resumo de erros no topo, como o DESIGN.md prescreve: recebe foco ao
            aparecer e cada item leva ao campo. Antes os erros globais ficavam
            no rodape do formulario, fora da tela depois do submit. */}
        <ErrorSummary errors={summaryErrors} />

        {/* ── Tipo de Documento & Emitente ── */}
        <FormSection legend="Informações do Documento">

            <div className="form-grid form-grid--2col">
              <div className="doc-type-group">
                <label className="field-label">Tipo de Documento</label>
                <div className="doc-type-options">
                  {(['NFE', 'NFSE', 'NFCE'] as DocType[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`doc-type-btn ${docType === type ? 'doc-type-btn--active' : ''}`}
                      onClick={() => setDocType(type)}
                    >
                      <span className="doc-type-btn__label">{docTypeLabel[type]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Select
                id="emit-emitter"
                label="Emitente"
                value={emitterId}
                error={fieldErrors['emitter']}
                onChange={(e) => {
                  setEmitterId(e.target.value);
                  setBranchId('');
                  clearFieldError('emitter');
                  clearFieldError('branch');
                }}
                placeholder="Selecione a empresa emitente"
              >
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name} ({company.cnpj})
                  </option>
                ))}
              </Select>

              <Select
                id="emit-branch"
                label="Filial (Estabelecimento)"
                value={branchId}
                error={fieldErrors['branch']}
                onChange={(e) => {
                  setBranchId(e.target.value);
                  clearFieldError('branch');
                }}
                placeholder={emitterId ? 'Selecione a filial' : 'Selecione a empresa primeiro'}
                disabled={!emitterId || branches.length === 0}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                  </option>
                ))}
              </Select>
            </div>
        </FormSection>

        {/* ── Destinatário ── */}
        {docType !== 'NFCE' && (
          <FormSection legend="Destinatário">

              <div className="form-grid form-grid--2col">
                <div className="autocomplete-wrapper" ref={destinataryRef}>
                  <div className="field-label">Buscar Destinatário</div>
                  <Input
                    id="emit-destinatary"
                    placeholder="Digite nome, CPF ou CNPJ..."
                    value={destinatarySearch}
                    error={fieldErrors['destinatary']}
                    onChange={(e) => {
                      setDestinatarySearch(e.target.value);
                      setShowDestinataryDropdown(true);
                      clearFieldError('destinatary');
                      if (!e.target.value) {
                        setDestinataryId('');
                        setDestinataryName('');
                        setDestinataryDoc('');
                      }
                    }}
                    onFocus={() => {
                      if (destinatarySearch.length >= 3) setShowDestinataryDropdown(true);
                    }}
                    helperText="Mínimo de 3 caracteres para busca"
                  />

                  {showDestinataryDropdown && parties.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {partiesLoading && <div className="autocomplete-loading">Buscando...</div>}
                      {!partiesLoading &&
                        parties.map((party) => (
                          <button
                            key={party.id}
                            type="button"
                            className="autocomplete-item"
                            onClick={() => {
                              setDestinataryId(party.id);
                              setDestinataryName(party.name);
                              setDestinataryDoc(party.cnpj ?? party.cpf ?? '');
                              setDestinatarySearch(party.name);
                              setShowDestinataryDropdown(false);
                            }}
                          >
                            <span className="autocomplete-item__name">{party.name}</span>
                            {party.cnpj && (
                              <span className="autocomplete-item__doc">{party.cnpj}</span>
                            )}
                            {party.cpf && (
                              <span className="autocomplete-item__doc">{party.cpf}</span>
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                <div className="selected-destinatary">
                  {destinataryId ? (
                    <div className="destinatary-info">
                      <span className="destinatary-info__name">{destinataryName}</span>
                      {destinataryDoc && (
                        <span className="destinatary-info__doc">{destinataryDoc}</span>
                      )}
                    </div>
                  ) : (
                    <div className="destinatary-placeholder">
                      <span>Nenhum destinatário selecionado</span>
                    </div>
                  )}
                </div>
              </div>
          </FormSection>
        )}

        {/* NFC-e allows optional consumer CPF */}
        {docType === 'NFCE' && (
          <FormSection legend="Consumidor (opcional para NFC-e)">
              <div className="form-grid form-grid--2col">
                <div className="autocomplete-wrapper" ref={destinataryRef}>
                  <Input
                    placeholder="Digite nome ou CPF..."
                    value={destinatarySearch}
                    onChange={(e) => {
                      setDestinatarySearch(e.target.value);
                      setShowDestinataryDropdown(true);
                    }}
                    onFocus={() => {
                      if (destinatarySearch.length >= 3) setShowDestinataryDropdown(true);
                    }}
                    helperText="Deixe em branco para consumidor não identificado"
                  />

                  {showDestinataryDropdown && parties.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {partiesLoading && <div className="autocomplete-loading">Buscando...</div>}
                      {!partiesLoading &&
                        parties.map((party) => (
                          <button
                            key={party.id}
                            type="button"
                            className="autocomplete-item"
                            onClick={() => {
                              setDestinataryId(party.id);
                              setDestinataryName(party.name);
                              setDestinataryDoc(party.cnpj ?? party.cpf ?? '');
                              setDestinatarySearch(party.name);
                              setShowDestinataryDropdown(false);
                            }}
                          >
                            <span className="autocomplete-item__name">{party.name}</span>
                            {party.cpf && (
                              <span className="autocomplete-item__doc">{party.cpf}</span>
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
          </FormSection>
        )}

        {/* ── Itens da Nota ── */}
        <FormSection legend="Itens da Nota">
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="button" variant="outline" onClick={addItem}>
                + Adicionar Item
              </Button>
            </div>

            {items.length === 0 && (
              <div className="empty-items">
                <p>Nenhum item adicionado. Clique em &quot;Adicionar Item&quot; para começar.</p>
              </div>
            )}

            {items.length > 0 && (
              <div className="items-table-wrap">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th className="col-product">Produto</th>
                      <th className="col-ncm">NCM</th>
                      <th className="col-cfop">CFOP</th>
                      <th className="col-cst">CST</th>
                      <th className="col-qty">Qtd</th>
                      <th className="col-value">Valor Unit.</th>
                      <th className="col-discount">Desc.</th>
                      <th className="col-taxes">ICMS</th>
                      <th className="col-taxes">PIS</th>
                      <th className="col-taxes">COFINS</th>
                      <th className="col-taxes">IPI</th>
                      <th className="col-taxes">ISS</th>
                      <th className="col-action"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td className="col-product">
                          <div className="autocomplete-wrapper" ref={productRef}>
                            <Input
                              placeholder="Buscar produto..."
                              id={`emit-item-${index}-description`}
                              error={fieldErrors[`item-${index}-description`]}
                              value={
                                productSearchIndex === index
                                  ? productSearchTerm
                                  : item.description
                              }
                              onChange={(e) => {
                                setProductSearchIndex(index);
                                setProductSearchTerm(e.target.value);
                                setShowProductDropdown(true);
                                if (!e.target.value) {
                                  updateItem(index, 'description', '');
                                }
                              }}
                              onFocus={() => {
                                setProductSearchIndex(index);
                                setProductSearchTerm(item.description || '');
                                if (item.description && !productSearchTerm) {
                                  setProductSearchTerm(item.description);
                                }
                              }}
                            />

                            {showProductDropdown &&
                              productSearchIndex === index &&
                              products.length > 0 && (
                                <div className="autocomplete-dropdown autocomplete-dropdown--wide">
                                  {productsLoading && (
                                    <div className="autocomplete-loading">Buscando...</div>
                                  )}
                                  {!productsLoading &&
                                    products.map((product) => (
                                      <button
                                        key={product.id}
                                        type="button"
                                        className="autocomplete-item"
                                        onClick={() => {
                                          selectProduct(index, product);
                                        }}
                                      >
                                        <span className="autocomplete-item__name">
                                          {product.name}
                                        </span>
                                        <span className="autocomplete-item__doc">
                                          {product.sku}
                                          {product.ncmCode ? ` - ${product.ncmCode}` : ''}
                                        </span>
                                      </button>
                                    ))}
                                </div>
                              )}
                          </div>
                        </td>

                        <td className="col-ncm">
                          <Input
                            placeholder="NCM"
                            id={`emit-item-${index}-ncm`}
                              error={fieldErrors[`item-${index}-ncm`]}
                            value={item.ncm}
                            onChange={(e) => updateItem(index, 'ncm', e.target.value)}
                            inputSize="sm"
                          />
                        </td>

                        <td className="col-cfop">
                          <Select
                            value={item.cfop}
                            onChange={(e) => updateItem(index, 'cfop', e.target.value)}
                            selectSize="sm"
                            aria-label="CFOP"
                          >
                            {commonCfops.map((cfop) => (
                              <option key={cfop.value} value={cfop.value}>
                                {cfop.label}
                              </option>
                            ))}
                          </Select>
                        </td>

                        <td className="col-cst">
                          <Select
                            value={item.cst}
                            onChange={(e) => {
                              updateItem(index, 'cst', e.target.value);
                              // Trigger tax recalculation on CST change
                              updateItem(index, 'quantity', item.quantity);
                            }}
                            selectSize="sm"
                            aria-label="CST"
                          >
                            {commonCstsNfe.map((cst) => (
                              <option key={cst} value={cst}>
                                {cst}
                              </option>
                            ))}
                          </Select>
                        </td>

                        <td className="col-qty">
                          <Input
                            type="number"
                            min="0"
                            step="0.001"
                            id={`emit-item-${index}-quantity`}
                              error={fieldErrors[`item-${index}-quantity`]}
                            value={String(item.quantity)}
                            onChange={(e) =>
                              updateItem(index, 'quantity', parseFloat(e.target.value) || 0)
                            }
                            inputSize="sm"
                            aria-label="Quantidade"
                          />
                        </td>

                        <td className="col-value">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            id={`emit-item-${index}-unitValue`}
                              error={fieldErrors[`item-${index}-unitValue`]}
                            value={String(item.unitValue)}
                            onChange={(e) =>
                              updateItem(index, 'unitValue', parseFloat(e.target.value) || 0)
                            }
                            inputSize="sm"
                            aria-label="Valor Unitário"
                          />
                        </td>

                        <td className="col-discount">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={String(item.discount)}
                            onChange={(e) =>
                              updateItem(index, 'discount', parseFloat(e.target.value) || 0)
                            }
                            inputSize="sm"
                            aria-label="Desconto"
                          />
                        </td>

                        <td className="col-taxes">
                          <span className="tax-value">{formatCurrency(item.icms)}</span>
                        </td>
                        <td className="col-taxes">
                          <span className="tax-value">{formatCurrency(item.pis)}</span>
                        </td>
                        <td className="col-taxes">
                          <span className="tax-value">{formatCurrency(item.cofins)}</span>
                        </td>
                        <td className="col-taxes">
                          <span className="tax-value">{formatCurrency(item.ipi)}</span>
                        </td>
                        <td className="col-taxes">
                          <span className="tax-value">{formatCurrency(item.iss)}</span>
                        </td>

                        <td className="col-action">
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => removeItem(index)}
                            aria-label={`Remover item ${index + 1}`}
                          >
                            ×
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </FormSection>

        {/* ── Totals ── */}
        {items.length > 0 && (
          <FormSection
            legend="Totais"
            description={(() => {
              const seq = sequences.find(
                (s) => s.entity === 'INVOICE' || s.entity === 'NFE' || s.entity === 'NF-E',
              ) ?? sequences[0];
              return seq ? `Próx. número: ${seq.format.replace(/\{+|\}+/g, '').replace(/seq/, String(seq.currentSequence + 1))}` : null;
            })()}
          >
              <div className="totals-grid">
                <div className="total-row">
                  <span className="total-row__label">Valor Total dos Itens</span>
                  <span className="total-row__value">{formatCurrency(totals.totalValue)}</span>
                </div>
                <div className="total-row">
                  <span className="total-row__label">Desconto Total</span>
                  <span className="total-row__value total-row__value--discount">
                    {formatCurrency(totals.totalDiscount)}
                  </span>
                </div>
                <div className="total-row">
                  <span className="total-row__label">ICMS</span>
                  <span className="total-row__value">{formatCurrency(totals.icms)}</span>
                </div>
                <div className="total-row">
                  <span className="total-row__label">PIS</span>
                  <span className="total-row__value">{formatCurrency(totals.pis)}</span>
                </div>
                <div className="total-row">
                  <span className="total-row__label">COFINS</span>
                  <span className="total-row__value">{formatCurrency(totals.cofins)}</span>
                </div>
                <div className="total-row">
                  <span className="total-row__label">IPI</span>
                  <span className="total-row__value">{formatCurrency(totals.ipi)}</span>
                </div>
                <div className="total-row">
                  <span className="total-row__label">ISS</span>
                  <span className="total-row__value">{formatCurrency(totals.iss)}</span>
                </div>
                <div className="total-row total-row--grand">
                  <span className="total-row__label">Valor Total da Nota</span>
                  <span className="total-row__value total-row__value--grand">
                    {formatCurrency(grandTotal)}
                  </span>
                </div>
              </div>
          </FormSection>
        )}

        {/* ── Errors ── */}
        {/* ── Actions ── */}
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: '/fiscal/invoices' }).catch(console.error)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={items.length === 0}
          >
            Emitir
          </Button>
        </div>
      </form>

      {/* ── Confirmation Modal ── */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Emissão"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              Confirmar e Emitir
            </Button>
          </>
        }
      >
        <div className="confirm-modal-content">
          <p>Confirme os dados da emissão:</p>
          <ul className="confirm-details">
            <li>
              <strong>Tipo:</strong> {docTypeLabel[docType]}
            </li>
            {companies.find((c) => c.id === emitterId)?.name && (
              <li>
                <strong>Emitente:</strong> {companies.find((c) => c.id === emitterId)?.name}
              </li>
            )}
            {branches.find((b) => b.id === branchId)?.name && (
              <li>
                <strong>Filial:</strong> {branches.find((b) => b.id === branchId)?.name} ({branches.find((b) => b.id === branchId)?.code})
              </li>
            )}
            <li>
              <strong>Itens:</strong> {items.length}
            </li>
            <li>
              <strong>Valor Total:</strong> {formatCurrency(grandTotal)}
            </li>
            {destinataryName && (
              <li>
                <strong>Destinatário:</strong> {destinataryName}
              </li>
            )}
          </ul>
          <p className="confirm-warning">
            Após confirmada, a nota será transmitida à SEFAZ para autorização.
          </p>
        </div>
      </Modal>

      {/* ── Styles ── */}
      <style>{`
        .fiscal-emit-page {
          padding: 1.5rem;
          max-width: 1200px;
        }

        .breadcrumb {
          font-size: 0.8125rem;
          color: #43474e;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .breadcrumb a {
          color: #002045;
          text-decoration: none;
        }
        .breadcrumb a:hover {
          text-decoration: underline;
        }
        .breadcrumb-separator {
          color: #c4c6cf;
        }

        .form-grid {
          display: grid;
          gap: 1rem;
        }
        .form-grid--2col {
          grid-template-columns: 1fr 1fr;
        }

        .field-label {
          font-size: 12px;
          font-weight: 500;
          color: #43474e;
          margin-bottom: 0.375rem; font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.02em;}

        /* ── Doc type buttons ── */
        .doc-type-group {
          display: flex;
          flex-direction: column;
        }
        .doc-type-options {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .doc-type-btn {
          flex: 1;
          min-width: 160px;
          padding: 0.75rem 1rem;
          border: 1px solid #c4c6cf;
          border-radius: 4px;
          background: #ffffff;
          cursor: pointer;
          font-family: inherit;
          font-size: 0.8125rem;
          color: #43474e;
          transition: all 0.15s ease;
          text-align: center;
        }
        .doc-type-btn:hover {
          border-color: #002045;
          background: #f2f4f6;
        }
        .doc-type-btn--active {
          border-color: #002045;
          background: #d6e3ff;
          color: #002045;
          font-weight: 600;
        }

        /* ── Autocomplete ── */
        .autocomplete-wrapper {
          position: relative;
        }
        .autocomplete-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 100;
          background: #ffffff;
          border: 1px solid #c4c6cf;
          border-radius: 4px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          max-height: 240px;
          overflow-y: auto;
          margin-top: 2px;
        }
        .autocomplete-dropdown--wide {
          min-width: 360px;
        }
        .autocomplete-loading {
          padding: 0.625rem;
          text-align: center;
          color: #74777f;
          font-size: 0.8125rem;
        }
        .autocomplete-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.625rem 0.75rem;
          border: none;
          background: none;
          cursor: pointer;
          font-family: inherit;
          font-size: 0.8125rem;
          text-align: left;
          border-bottom: 1px solid #eceef0;
          color: #191c1e;
        }
        .autocomplete-item:last-child {
          border-bottom: none;
        }
        .autocomplete-item:hover {
          background: #f2f4f6;
        }
        .autocomplete-item__name {
          font-weight: 500;
        }
        .autocomplete-item__doc {
          font-size: 0.75rem;
          color: #74777f;
          font-family: monospace;
          margin-left: 0.5rem;
        }

        /* ── Selected destinatary ── */
        .selected-destinatary {
          display: flex;
          align-items: flex-end;
          padding-bottom: 0.375rem;
        }
        .destinatary-info {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          padding: 0.5rem 0.75rem;
          background: #f2f4f6;
          border: 1px solid #d6e3ff;
          border-radius: 4px;
          width: 100%;
        }
        .destinatary-info__name {
          font-size: 0.875rem;
          font-weight: 600;
          color: #191c1e;
        }
        .destinatary-info__doc {
          font-size: 0.75rem;
          color: #43474e;
          font-family: monospace;
        }
        .destinatary-placeholder {
          color: #74777f;
          font-size: 0.8125rem;
          font-style: italic;
        }

        /* ── Items table ── */
        .items-table-wrap {
          overflow-x: auto;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8125rem;
          min-width: 1000px;
        }
        .items-table th {
          text-align: left;
          padding: 0.5rem 0.5rem;
          font-weight: 600;
          color: #43474e;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 2px solid #e0e3e5;
          white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .items-table td {
          padding: 0.375rem 0.375rem;
          vertical-align: top;
          border-bottom: 1px solid #eceef0;
        }
        .items-table tr:last-child td {
          border-bottom: none;
        }
        .items-table tbody tr:hover {
          background: #f2f4f6;
        }

        .col-product { min-width: 220px; }
        .col-ncm { min-width: 80px; width: 80px; }
        .col-cfop { min-width: 100px; width: 100px; }
        .col-cst { min-width: 60px; width: 60px; }
        .col-qty { min-width: 70px; width: 70px; }
        .col-value { min-width: 100px; width: 100px; }
        .col-discount { min-width: 80px; width: 80px; }
        .col-taxes { min-width: 80px; width: 80px; text-align: right; }
        .col-action { width: 50px; }

        /* Erros inline compactos dentro das células da tabela */
        .items-table td input[data-error="true"] {
          border-color: #ba1a1a;
        }
        .items-table td p,
        .items-table td [class*="error"] {
          font-size: 0.625rem !important;
          line-height: 1.1 !important;
          margin: 2px 0 0 !important;
          white-space: normal;
        }

        .tax-value {
          font-size: 0.75rem;
          font-weight: 500;
          color: #191c1e;
          display: block;
          text-align: right;
          padding: 0.5rem 0;
        }

        .empty-items {
          padding: 3rem 0;
          text-align: center;
          color: #74777f;
          font-size: 0.875rem;
        }

        /* ── Totals card ── */
        .totals-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem 2rem;
          max-width: 480px;
          margin-left: auto;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.25rem 0;
        }
        .total-row__label {
          font-size: 0.8125rem;
          color: #43474e;
        }
        .total-row__value {
          font-size: 0.875rem;
          font-weight: 600;
          color: #191c1e;
        }
        .total-row__value--discount {
          color: #ba1a1a;
        }
        .total-row--grand {
          border-top: 2px solid #191c1e;
          padding-top: 0.75rem;
          margin-top: 0.5rem;
          grid-column: 1 / -1;
        }
        .total-row__value--grand {
          font-size: 1.125rem;
          color: #002045;
        }

        /* ── Errors ── */
        .form-errors {
          padding: 1rem;
          background: #ffdad6;
          border: 1px solid #ffb4ab;
          border-radius: 4px;
          margin: 1rem 0;
        }
        .form-errors p {
          color: #ba1a1a;
          font-size: 0.875rem;
          margin: 0.25rem 0;
        }

        /* ── Actions ── */
        .form-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          margin-top: 1.5rem;
        }

        /* ── Confirmation modal ── */
        .confirm-modal-content p {
          font-size: 0.875rem;
          color: #43474e;
          margin: 0 0 0.75rem;
        }
        .confirm-details {
          list-style: none;
          padding: 0;
          margin: 0 0 1rem;
        }
        .confirm-details li {
          padding: 0.375rem 0;
          font-size: 0.875rem;
          color: #191c1e;
          border-bottom: 1px solid #eceef0;
        }
        .confirm-details li:last-child {
          border-bottom: none;
        }
        .confirm-details strong {
          display: inline-block;
          min-width: 100px;
          color: #43474e;
        }
        .confirm-warning {
          font-size: 0.75rem !important;
          color: #74777f !important;
          font-style: italic;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .fiscal-emit-page {
            padding: 1rem;
          }
          .form-grid--2col {
            grid-template-columns: 1fr;
          }
          .doc-type-options {
            flex-direction: column;
          }
          .doc-type-btn {
            min-width: unset;
          }
          .totals-grid {
            max-width: 100%;
            margin-left: 0;
          }
        }

        /* ── Success banner ── */
        .success-banner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: #d3f5d6;
          border: 1px solid #b6ebbb;
          border-left: 4px solid #2e7d32;
          border-radius: 8px;
          padding: 0.875rem 1rem;
          margin-bottom: 1.25rem;
          flex-wrap: wrap;
        }
        .success-banner strong {
          color: #113d15;
          font-size: 0.9375rem;
        }
        .success-banner span {
          color: #2e7d32;
          font-size: 0.875rem;
          flex: 1;
          min-width: 200px;
        }
      `}</style>
    </div>
  );
}
